import { useEffect, useMemo, useState } from "react";
import { http, downloadAuthenticated } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Calendar, RefreshCw, FileText, ChevronDown, ChevronUp, FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

/**
 * Διαχειριστικός πίνακας: όλες οι διαγραφές τραπεζιών (audit
 * action="table.delete") μέσα σε ένα date range. Δείχνει ποιος, πότε,
 * ποιο τραπέζι, πόσα €, και (expandable) τα συγκεκριμένα είδη που
 * ακυρώθηκαν. Default: τελευταίες 7 ημέρες.
 */
export default function CancelledTablesPanel() {
  const [from, setFrom] = useState(daysAgoISO(7));
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState({ rows: [], count: 0, total_amount: 0 });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const load = async () => {
    setBusy(true);
    try {
      const r = await http.get("/reports/cancelled-tables", {
        params: { date_from: from || undefined, date_to: to || undefined },
      });
      setData(r.data);
    } catch {
      setData({ rows: [], count: 0, total_amount: 0 });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    // Group by user — quick «ποιος ακύρωσε περισσότερα» chips.
    const m = new Map();
    for (const r of data.rows) {
      const k = r.user_name || "—";
      const cur = m.get(k) || { count: 0, total: 0 };
      m.set(k, { count: cur.count + 1, total: cur.total + (r.amount || 0) });
    }
    return Array.from(m.entries())
      .map(([user, v]) => ({ user, ...v }))
      .sort((a, b) => b.total - a.total);
  }, [data.rows]);

  const toggle = (i) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  return (
    <Card className="p-5 space-y-4" data-testid="cancelled-tables-panel">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="grid gap-1">
          <Label className="text-xs">Από</Label>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="font-mono h-10 w-44"
            data-testid="cancelled-from"
          />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Έως</Label>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="font-mono h-10 w-44"
            data-testid="cancelled-to"
          />
        </div>
        <Button
          onClick={load}
          disabled={busy}
          className="h-10 bg-red-600 hover:bg-red-700"
          data-testid="cancelled-refresh"
        >
          <RefreshCw size={16} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Φόρτωση…" : "Ανανέωση"}
        </Button>
        <Button
          onClick={() => {
            const params = { fmt: "xlsx" };
            if (from) params.date_from = from;
            if (to) params.date_to = to;
            const stamp = new Date().toISOString().slice(0, 10);
            downloadAuthenticated(
              "/reports/cancelled-tables/export",
              params,
              `cancelled-tables-${stamp}.xlsx`,
              { folder: "reports" },
            )
              .then((r) => toast.success(
                r.viaCompanion
                  ? `Αποθηκεύτηκε: ${r.savedTo}`
                  : "Λήφθηκε το XLSX",
              ))
              .catch((e) => toast.error(
                e?.message?.includes("Companion")
                  ? "Ο Companion agent δεν αποκρίνεται (πόρτα 9100)"
                  : "Αποτυχία εξαγωγής",
              ));
          }}
          variant="outline"
          className="h-10 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid="cancelled-export-xlsx"
        >
          <FileSpreadsheet size={16} className="mr-1.5" /> Excel
        </Button>
        <Button
          onClick={() => {
            const params = { fmt: "csv" };
            if (from) params.date_from = from;
            if (to) params.date_to = to;
            const stamp = new Date().toISOString().slice(0, 10);
            downloadAuthenticated(
              "/reports/cancelled-tables/export",
              params,
              `cancelled-tables-${stamp}.csv`,
              { folder: "reports" },
            )
              .then((r) => toast.success(
                r.viaCompanion
                  ? `Αποθηκεύτηκε: ${r.savedTo}`
                  : "Λήφθηκε το CSV",
              ))
              .catch((e) => toast.error(
                e?.message?.includes("Companion")
                  ? "Ο Companion agent δεν αποκρίνεται (πόρτα 9100)"
                  : "Αποτυχία εξαγωγής",
              ));
          }}
          variant="outline"
          className="h-10 border-blue-300 text-blue-700 hover:bg-blue-50"
          data-testid="cancelled-export-csv"
        >
          <FileDown size={16} className="mr-1.5" /> CSV
        </Button>
        <div className="ml-auto flex items-center gap-2 text-sm">
          <div className="px-3 py-1.5 rounded-full bg-red-50 border-2 border-red-300 text-red-700 font-bold">
            {data.count} ακυρώσεις
          </div>
          <div className="px-3 py-1.5 rounded-full bg-amber-50 border-2 border-amber-300 text-amber-800 font-bold">
            {Number(data.total_amount || 0).toFixed(2)} €
          </div>
        </div>
      </div>

      {/* per-user chips */}
      {grouped.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wider text-slate-500">Ανά χρήστη:</span>
          {grouped.slice(0, 8).map((g) => (
            <span
              key={g.user}
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-xs font-bold text-slate-700"
            >
              <span className="text-slate-900">{g.user}</span>
              <span className="text-slate-500">×{g.count}</span>
              <span className="text-red-700">{g.total.toFixed(2)} €</span>
            </span>
          ))}
        </div>
      )}

      {/* table */}
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 uppercase text-[11px]">
            <tr>
              <th className="text-left px-3 py-2">Ημ/νία</th>
              <th className="text-left px-3 py-2">Χρήστης</th>
              <th className="text-left px-3 py-2">Τραπέζι</th>
              <th className="text-right px-3 py-2">Είδη</th>
              <th className="text-right px-3 py-2">VOID Lines</th>
              <th className="text-right px-3 py-2">Αξία (€)</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center py-6 text-slate-400 italic">
                  Καμία ακύρωση τραπεζιού στο επιλεγμένο εύρος.
                </td>
              </tr>
            )}
            {data.rows.flatMap((r, i) => {
              const isOpen = expanded.has(i);
              const dt = r.timestamp ? new Date(r.timestamp) : null;
              const out = [
                (
                  <tr
                    key={`row-${i}`}
                    className={`border-t border-slate-100 hover:bg-slate-50 ${isOpen ? "bg-slate-50" : ""}`}
                    data-testid={`cancelled-row-${i}`}
                  >
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                      <Calendar size={12} className="inline mr-1 text-slate-400" />
                      {dt
                        ? `${dt.toLocaleDateString("el-GR")} ${dt.toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" })}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2 font-bold text-slate-700">{r.user_name}</td>
                    <td className="px-3 py-2">
                      <Trash2 size={12} className="inline mr-1 text-red-500" />
                      <span className="font-bold">{r.table_name}</span>
                      {r.device_number != null && (
                        <span className="ml-1.5 text-xs text-slate-400">#{r.device_number}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{r.items_count || 0}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      <span className={r.fiscal_voids > 0 ? "text-red-600 font-bold" : "text-slate-400"}>
                        {r.fiscal_voids}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-amber-700">
                      {Number(r.amount || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {(r.items || []).length > 0 && (
                        <button
                          onClick={() => toggle(i)}
                          className="text-slate-500 hover:text-slate-900"
                          data-testid={`cancelled-expand-${i}`}
                        >
                          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                      )}
                    </td>
                  </tr>
                ),
              ];
              if (isOpen && (r.items || []).length > 0) {
                out.push(
                  <tr key={`exp-${i}`} className="bg-slate-50 border-t border-slate-100">
                    <td colSpan="7" className="px-6 py-3">
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                        <FileText size={12} /> Είδη που ακυρώθηκαν
                      </div>
                      <ul className="text-sm space-y-0.5">
                        {r.items.map((it, j) => (
                          <li
                            key={j}
                            className="flex items-center justify-between gap-3 font-mono"
                            data-testid={`cancelled-item-${i}-${j}`}
                          >
                            <span className="text-slate-700">
                              {it.qty} × {it.description}
                            </span>
                            <span className="text-slate-500">
                              {Number(it.unit_price || 0).toFixed(2)} €
                            </span>
                            <span className="text-amber-700 font-bold w-20 text-right">
                              {Number(it.line_total || 0).toFixed(2)} €
                            </span>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                );
              }
              return out;
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
